import React, { useState } from 'react';
import { X, FileDown } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { IndentPDF } from './IndentPDF';

export const ExportPopup = ({ onClose, exportEndpoint, indentData, customFilename = 'export' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const processIndentData = (rawData) => {
    if (!rawData) return [];
    
    const dataArray = Array.isArray(rawData) ? rawData : [rawData];
    
    return dataArray.filter(Boolean).map(item => {
      // Group items by category and process dates
      const groupedItems = (item?.IndentItems || []).reduce((acc, indentItem) => {
        const processedItem = {
          name: indentItem?.ItemName || 'N/A',
          category: indentItem?.CategoryName || 'N/A',
          au: indentItem?.AU || 'N/A',
          quantity: indentItem?.Quantity || 0,
          stock: indentItem?.Stock || 0,
          description: indentItem?.Description || '__',
          promisedDays: indentItem?.NormalDays || 'N/A',
          // Explicitly handle the CommitedDate from the API response
          committedDate: indentItem?.CommitedDate || 'N/A'
        };
        
        // Log the date for debugging
        console.log('Processing CommitedDate:', indentItem?.CommitedDate);
        
        return [...acc, processedItem];
      }, []);

      return {
        indentId: item?.IndentID || 'N/A',
        creationDate: item?.CreationDate || 'N/A',
        projectName: item?.ProjectName || 'N/A',
        taskScheduled: item?.StageName || 'N/A',
        status: item?.Status || 'N/A',
        invoicingType: item?.TypeName || 'N/A',
        items: groupedItems
      };
    });
  };

  const handleExport = async (format) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (format === 'pdf') {
        const processedData = processIndentData(indentData);
        console.log("Processed data for PDF:", processedData);
        
        if (!processedData || (Array.isArray(processedData) && processedData.length === 0)) {
          throw new Error('No data available for export');
        }
        
        const blob = await pdf(<IndentPDF data={processedData} />).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${customFilename}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        onClose();
        return;
      }

      const response = await fetch(`${exportEndpoint}?format=${format}`);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${customFilename}.${format}`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onClose();
    } catch (err) {
      setError('Failed to export data. Please try again.');
      console.error('Export error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Export Data</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
          <button
            onClick={() => handleExport('csv')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as CSV
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors duration-200"
          >
            <FileDown className="mr-2 w-4 h-4" />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
};